import { useState } from "react";
import {
  Area, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";
import { TrendUp } from "@phosphor-icons/react";
import { EmptyState } from "@/components/shared";

const BANDS = [
  { min: 9, label: "Excellent", tone: "text-success" },
  { min: 7, label: "Good", tone: "text-success" },
  { min: 5, label: "Average", tone: "text-[hsl(28_82%_38%)]" },
  { min: 3, label: "Below average", tone: "text-destructive" },
  { min: 0, label: "Needs attention", tone: "text-destructive" },
];
const band = (v) => BANDS.find((b) => v >= b.min) || BANDS[BANDS.length - 1];

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const value = payload[0].value;
  return (
    <div className="glass rounded-xl px-4 py-3 shadow-elevated"
         data-testid="trend-tooltip">
      <p className="label-caps">{label}</p>
      <p className="mt-1 font-mono text-2xl tabular-nums text-navy">
        {value}<span className="text-xs text-muted-foreground">/10</span>
      </p>
      <p className={`text-[10px] uppercase tracking-[0.14em] ${band(value).tone}`}>{band(value).label}</p>
    </div>
  );
};

export const TrendPanel = ({ trend, subScores = [], score10, target = 8 }) => {
  const [showTarget, setShowTarget] = useState(true);
  const [showAverage, setShowAverage] = useState(false);
  const average = trend.length
    ? Number((trend.reduce((sum, r) => sum + (r.score10 || 0), 0) / trend.length).toFixed(2))
    : null;
  const first = trend[0]?.score10;
  const last = trend[trend.length - 1]?.score10;
  const delta = first != null && last != null ? Number((last - first).toFixed(2)) : null;

  return (
    <section className="panel overflow-hidden" data-testid="lec-overall">
      <div className="relative overflow-hidden bg-gradient-to-br from-navy via-navy to-deepblue px-6 py-7 text-white md:px-10">
        <div className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-gold/25 blur-3xl" />
        <div className="relative flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="label-caps text-white/60">Overall summary</p>
            <p className="mt-2 font-mono text-5xl tabular-nums" data-testid="trend-latest-score">
              {score10 ?? "—"}<span className="text-lg text-white/50">/10</span>
            </p>
            <p className="mt-1 text-xs uppercase tracking-[0.16em] text-[hsl(28_82%_38%)]">
              {score10 != null ? band(score10).label : "Not scored"}
            </p>
          </div>
          <div className="flex gap-6">
            <div>
              <p className="label-caps text-white/50">Periods</p>
              <p className="font-mono text-2xl tabular-nums">{trend.length}</p>
            </div>
            <div>
              <p className="label-caps text-white/50">Movement</p>
              <p className="flex items-center gap-1.5 font-mono text-2xl tabular-nums">
                <TrendUp size={18} className={delta >= 0 ? "text-success" : "text-destructive rotate-180"} />
                {delta == null ? "—" : `${delta > 0 ? "+" : ""}${delta}`}
              </p>
            </div>
            <div>
              <p className="label-caps text-white/50">Average</p>
              <p className="font-mono text-2xl tabular-nums">{average ?? "—"}</p>
            </div>
          </div>
        </div>
      </div>

      {trend.length === 0 ? (
        <EmptyState testid="lec-trend-empty" title="No trend yet"
                    message="Submit the survey across periods to plot the trend." />
      ) : (
        <div className="p-6 md:p-8">
          <div className="mb-4 flex flex-wrap gap-2">
            {[["Target line", showTarget, setShowTarget, "trend-toggle-target"],
              ["Average", showAverage, setShowAverage, "trend-toggle-average"]].map(
              ([label, on, set, testid]) => (
                <button key={label} data-testid={testid} onClick={() => set(!on)}
                        className={`rounded-full border px-4 py-2 text-[10px] uppercase tracking-[0.14em] transition-colors ${
                          on ? "border-navy bg-navy text-white" : "border-border text-navy hover:bg-muted"}`}>
                  {label}
                </button>
              ))}
          </div>

          <div className="h-80" data-testid="trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={trend} margin={{ top: 10, right: 12, bottom: 0, left: -18 }}>
                <defs>
                  <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(44 68% 50%)" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="hsl(44 68% 50%)" stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="period" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis domain={[0, 10]} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip content={<ChartTip />} cursor={{ stroke: "hsl(var(--navy))", strokeWidth: 24, strokeOpacity: 0.08 }} />
                <Legend wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em" }} />
                <Area type="monotone" dataKey="score10" name="Score" stroke="hsl(20 86% 47%)"
                      strokeWidth={3} fill="url(#trendFill)" animationDuration={1100}
                      activeDot={{ r: 7, strokeWidth: 3, stroke: "#fff" }} />
                {showAverage && average != null && (
                  <Line type="monotone" dataKey={() => average} name="Average" stroke="hsl(222 72% 46%)"
                        strokeWidth={2} strokeDasharray="6 4" dot={false} />
                )}
                {showTarget && (
                  <ReferenceLine y={target} stroke="hsl(186 74% 32%)" strokeDasharray="5 5"
                                 label={{ value: `Target ${target}`, position: "insideTopRight",
                                          fill: "hsl(184 62% 28%)", fontSize: 10 }} />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {subScores.length > 0 && (
            <div className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-2">
              {subScores.map((s) => (
                <div key={s.code} data-testid={`trend-sub-${s.code}`}
                     className="group rounded-2xl border border-border p-4 transition-colors hover:border-gold">
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs text-foreground/80">{s.name}</span>
                    <span className="font-mono text-sm tabular-nums text-navy">{s.score10 ?? "—"}</span>
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full bg-gradient-to-r from-gold to-teal transition-all duration-700"
                         style={{ width: `${((s.score10 ?? 0) / 10) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
